/**
 * Google Analytics 4 for the marketing site.
 *
 * Loaded on every marketing page (`Shell`) and every shared-statement page (`ShareShell`); a
 * `track()` call before the tag is on the page is a no-op. The measurement id comes from `VITE_GA_MEASUREMENT_ID`, so a build without one (local,
 * previews) has no analytics at all.
 *
 * Always on, by the owner's decision (2026-09-26): there is no switch and no stored choice. The
 * privacy and cookie policies disclose it.
 *
 * Page views are sent by this module, not by GA4's automatic `page_view`: the site is a single-page
 * app, and the automatic one fires for the first document only. `usePageViews()` reports each
 * client-side navigation, and `loadAnalytics()` reports the page it was called on. Keep GA4's
 * enhanced measurement "Page changes based on browser history events" **off** for this property,
 * or every navigation is counted twice.
 *
 * Firebase Analytics on the web is GA4 with the Firebase SDK in front of it; the SDK adds ~30 KB
 * and nothing this site needs, so the tag is loaded directly. The measurement id is the same
 * property either way.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Every event the site reports, so a report is written against a list rather than a string that
 * once appeared in a component. Parameters carry categories, never a person: no email, no share
 * token, no query typed into a search box.
 *
 * - `page_view`       — each page, including client-side navigation.
 * - `download_click`  — a link to an installer, a package or the Microsoft Store.
 *                       `channel`: `store` | `direct`; `platform`: the package's OS.
 * - `open_web_app`    — a link to my.transportbook.app; `placement` says which one.
 * - `demo_start` / `demo_submit` / `demo_error` — the demo request form.
 * - `demo_cta_click`  — the "Request a demo" call to action, before the form.
 * - `trial_start` / `trial_code_sent` / `trial_submit` / `trial_error` — the free-trial form:
 *                       first focus, OTP sent, organisation created, a refusal (`step` says where).
 * - `trial_cta_click`  — a "Start free trial" call to action; `placement` says which one.
 * - `password_reset_start` / `password_reset_submit` / `password_reset_error` — the forgot-password
 *                       form: first focus, password changed, a refusal (`step` says where).
 * - `faq_open`        — a support question expanded; `question` is the heading text.
 * - `demo_video_play` — the demo video's play button; `placement` says which page, `language`
 *                       which cut.
 * - `theme_toggle`    — the light/dark switch; `theme` is what was switched to.
 * - `share_view`      — a shared statement opened (`kind`, `audience`; never the token).
 */
export type AnalyticsEventName =
  | 'page_view'
  | 'download_click'
  | 'open_web_app'
  | 'demo_start'
  | 'demo_submit'
  | 'demo_error'
  | 'demo_cta_click'
  | 'trial_start'
  | 'trial_code_sent'
  | 'trial_submit'
  | 'trial_error'
  | 'trial_cta_click'
  | 'password_reset_start'
  | 'password_reset_submit'
  | 'password_reset_error'
  | 'faq_open'
  | 'demo_video_play'
  | 'theme_toggle'
  | 'share_view';

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

const measurementId = (): string | undefined => import.meta.env.VITE_GA_MEASUREMENT_ID || undefined;

function loaded(): boolean {
  return (
    typeof window !== 'undefined' &&
    Boolean(window.gtag && document.querySelector('script[data-ga]'))
  );
}

/** Reports an event. Silently does nothing until analytics are loaded. */
export function track(name: AnalyticsEventName, params: AnalyticsParams = {}) {
  if (!loaded()) return;
  window.gtag?.('event', name, params);
}

/** A page view for the current document, as GA4 would send it for a full load. */
export function trackPageView() {
  const isSharePage = /^\/s\/[^/]+(?:\/|$)/.test(window.location.pathname);
  track('page_view', {
    page_path: isSharePage ? '/s/:token' : window.location.pathname,
    page_title: document.title,
    // Share URLs contain their bearer token in the path. Keep this defense here as well as in
    // ShareShell so a future page-view caller cannot accidentally send the credential to GA4.
    page_location: isSharePage ? `${window.location.origin}/s/:token` : window.location.href,
  });
}

/**
 * Loads the tag, once.
 *
 * `sendPageView` exists for the shared-statement pages: their path carries the share token, and a
 * page view would ship that token to Google. Those pages load analytics with page views off and
 * report their own token-free event instead.
 */
export function loadAnalytics({ sendPageView = true }: { sendPageView?: boolean } = {}) {
  const id = measurementId();
  if (!id || typeof window === 'undefined' || document.querySelector('script[data-ga]')) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => window.dataLayer?.push(args);
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  window.gtag('config', id, {
    anonymize_ip: true,
    // Page views are this module's, so a client-side navigation counts exactly once.
    send_page_view: false,
    // Events from a developer's machine show in GA4's DebugView and nowhere else.
    ...(import.meta.env.DEV ? { debug_mode: true } : {}),
  });
  const script = document.createElement('script');
  script.async = true;
  script.dataset.ga = 'true';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.append(script);
  if (sendPageView) trackPageView();
}

/**
 * Reports a page view on every client-side navigation. The page the document loaded with is
 * reported by `loadAnalytics()` instead.
 *
 * Runs after the page's own effects, so `<Seo>` has already set `document.title`.
 */
export function usePageViews() {
  const { pathname } = useLocation();
  // The path last reported, seeded with the one the document loaded with. A ref rather than a
  // "first run" flag, so React's development double-invocation of effects cannot report twice.
  const reported = useRef(pathname);
  useEffect(() => {
    if (reported.current === pathname) return;
    reported.current = pathname;
    trackPageView();
  }, [pathname]);
}
